import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, Send, Loader2, HeartPulse, Trash2, Shield, AlertTriangle,
  Sparkles, Leaf, Heart, BookOpen,
} from "lucide-react";
import { VATE } from "@/constants/testIds";
import { chatVate, chatVateHistory, chatVateClear } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { PLAYLISTS, ACCENTS } from "@/lib/tracks";
import MusicPicker from "@/components/MusicPicker";

const VATE_SESSION_KEY = "astrovoyance.vate_session";
const VATE_AGE_KEY = "astrovoyance.vate_age_confirmed"; // "adult" | "minor" | ""

export default function Vate() {
  const { lang, t, setLang } = useLang();
  // Vate is now a free emotional guide — no age restriction. Auto-confirm on mount.
  const [ageConfirmed, setAgeConfirmed] = useState(true);
  useEffect(() => { localStorage.setItem(VATE_AGE_KEY, "adult"); }, []);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(VATE_SESSION_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [crisis, setCrisis] = useState(false);
  const boxRef = useRef(null);

  // Load history when we have a session
  useEffect(() => {
    if (!sessionId) return;
    chatVateHistory(sessionId)
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [sessionId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const confirmAge = () => {
    localStorage.setItem(VATE_AGE_KEY, "adult");
    setAgeConfirmed(true);
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    const my = { sender: "user", message: trimmed, created_at: new Date().toISOString() };
    setMessages((m) => [...m, my]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatVate(trimmed, sessionId, lang, true);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(VATE_SESSION_KEY, r.session_id);
      }
      if (r.crisis_detected) setCrisis(true);
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(lang === "fr" ? "Le Vate écoute encore le vent. Réessayez." : "The Vate is still listening to the wind. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const wipeAndLeave = async () => {
    try { await chatVateClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(VATE_SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setCrisis(false);
    toast.success(lang === "fr" ? "Session effacée. Va en paix." : "Session wiped. Go in peace.");
  };

  const suggestions = useMemo(() => ([
    t("vate.suggestion.1"),
    t("vate.suggestion.2"),
    t("vate.suggestion.3"),
    t("vate.suggestion.4"),
    t("vate.suggestion.5"),
    t("vate.suggestion.6"),
  ]), [lang, t]);

  return (
    <div data-testid={VATE.page} className="vate-root min-h-screen relative">
      {/* Ambient backdrop — mossy forest with candle light */}
      <div className="vate-bg" aria-hidden="true">
        <div className="vate-vignette" />
        <div className="vate-motes" />
      </div>

      {/* Top bar */}
      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]"
                style={{ color: "var(--gold)" }}>
            <ArrowLeft size={14} /> {t("vate.back")}
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(72, 105, 78, 0.28)", border: "1px solid rgba(200, 230, 210, 0.25)" }}>
              <button onClick={() => setLang("fr")} type="button"
                      data-testid="vate-lang-fr"
                      className="px-2.5 py-1.5 transition-colors"
                      style={{ background: lang === "fr" ? "#e6d9a2" : "transparent",
                               color: lang === "fr" ? "#1a1108" : "#d7e8d8",
                               fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button"
                      data-testid="vate-lang-en"
                      className="px-2.5 py-1.5 transition-colors"
                      style={{ background: lang === "en" ? "#e6d9a2" : "transparent",
                               color: lang === "en" ? "#1a1108" : "#d7e8d8",
                               fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono px-3 py-2 rounded-full"
                  style={{ background: "rgba(28,50,30,0.55)", border: "1px solid rgba(200,230,210,0.2)", color: "#c8dbcb" }}>
              <Shield size={11} /> {t("vate.privacy.badge")}
            </span>
            <MusicPicker playlist={PLAYLISTS.vate} accent={ACCENTS.vate} testId="vate-music" persona="Vate" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-8">
        <div className="overline mb-4" style={{ color: "#c8dbcb" }}>
          <span className="kicker-line" />
          {t("vate.kicker")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f4ead0" }}>
          {t("vate.title.a")}
          <span className="italic" style={{ color: "#f0d97a" }}>{t("vate.title.b")}</span>
          {t("vate.title.c")}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#d7e8d8" }}>
          {t("vate.subtitle")}
        </p>
      </section>

      {/* Age gate OR chat */}
      {!ageConfirmed ? (
        <AgeGate onConfirm={confirmAge} t={t} />
      ) : (
        <>
          {/* Disclaimer */}
          <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 mb-6">
            <div className="vate-disclaimer">
              <AlertTriangle size={16} className="shrink-0" style={{ color: "#f0d97a" }} />
              <div>
                <strong className="block text-sm" style={{ color: "#f4ead0" }}>{t("vate.disclaimer.title")}</strong>
                <span className="text-xs" style={{ color: "#c8dbcb" }}>{t("vate.disclaimer.body")}</span>
              </div>
            </div>
          </section>

          {/* Mode badge (18+ only, no switching) */}
          <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 mb-6">
            <span data-testid={VATE.modeBadge}
                  className="inline-flex items-center gap-2 text-[11px] font-mono px-3 py-2 rounded-full"
                  style={{ background: "rgba(212,175,55,0.18)",
                           color: "#f0d97a",
                           border: "1px solid rgba(240,217,122,0.35)" }}>
              <BookOpen size={12} />
              {t("vate.mode.badge")}
            </span>
          </section>

          {/* Chat area */}
          <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-24">
            {crisis && (
              <div data-testid={VATE.crisisBanner} className="vate-crisis mb-4">
                <HeartPulse size={16} />
                <span className="text-sm">{t("vate.crisis.banner")}</span>
              </div>
            )}

            <div className="vate-chat" data-testid={VATE.chatBox}>
              <div ref={boxRef} className="vate-messages">
                {messages.length === 0 && (
                  <div className="vate-empty">
                    <Leaf size={22} style={{ color: "#f0d97a" }} />
                    <p className="mt-3 font-serif text-xl" style={{ color: "#f4ead0" }}>
                      {lang === "fr"
                        ? "Le feu du foyer craque. Le Vate t'écoute."
                        : "The hearth fire crackles. The Vate is listening."}
                    </p>
                    <p className="mt-4 text-sm font-mono uppercase tracking-[0.2em]" style={{ color: "#c8dbcb" }}>
                      {t("vate.suggestions.title")}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2 justify-center">
                      {suggestions.map((s, i) => (
                        <button key={i} type="button"
                                data-testid={VATE.suggestion(i)}
                                onClick={() => send(s)}
                                className="vate-chip">
                          <Sparkles size={11} /> {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`vate-msg ${m.sender === "user" ? "user" : "advisor"}`}>
                    <div className="vate-msg-inner">
                      {m.sender === "advisor" ? (
                        <div className="vate-md"><ReactMarkdown>{m.message}</ReactMarkdown></div>
                      ) : (
                        <span>{m.message}</span>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="vate-msg advisor">
                    <div className="vate-msg-inner inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span className="text-sm" style={{ color: "#c8dbcb" }}>
                        {lang === "fr" ? "Le Vate rassemble les braises…" : "The Vate gathers the embers…"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <form className="vate-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <textarea
                  data-testid={VATE.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder={t("vate.placeholder")}
                  rows={2}
                  className="vate-textarea"
                  disabled={loading}
                />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={wipeAndLeave}
                          data-testid={VATE.clear}
                          className="vate-btn-ghost"
                          title={t("vate.clear")}>
                    <Trash2 size={14} /> <span className="hidden sm:inline">{t("vate.clear")}</span>
                  </button>
                  <button type="submit" data-testid={VATE.send}
                          disabled={loading || !input.trim()}
                          className="vate-btn-primary">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {t("vate.send")}
                  </button>
                </div>
              </form>
              <ChatDisclaimer testId="vate-ai-disclaimer" />
            </div>
          </section>
        </>
      )}

      <style>{`
        .vate-root {
          background: radial-gradient(ellipse at top, #12211a 0%, #0a1610 55%, #050908 100%);
          color: #f4ead0;
          overflow-x: hidden;
        }
        .vate-bg {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 18% 22%, rgba(240, 217, 122, 0.16) 0%, transparent 45%),
            radial-gradient(circle at 82% 78%, rgba(72, 140, 88, 0.14) 0%, transparent 42%),
            linear-gradient(180deg, #101d16 0%, #06100a 100%);
        }
        .vate-vignette {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.65) 100%);
        }
        .vate-motes {
          position: absolute; inset: 0;
          background-image:
            radial-gradient(circle at 14% 24%, rgba(240,217,122,0.6) 0.4px, transparent 1px),
            radial-gradient(circle at 68% 44%, rgba(200,230,210,0.5) 0.4px, transparent 1px),
            radial-gradient(circle at 32% 68%, rgba(240,217,122,0.7) 0.4px, transparent 1px),
            radial-gradient(circle at 82% 12%, rgba(200,230,210,0.55) 0.4px, transparent 1px);
          animation: moteFloat 11s ease-in-out infinite;
          opacity: 0.65;
        }
        @keyframes moteFloat {
          0%,100% { transform: translateY(0); opacity: 0.55; }
          50%     { transform: translateY(-10px); opacity: 0.9; }
        }
        .vate-disclaimer {
          display: flex; gap: 12px; align-items: flex-start;
          padding: 14px 18px; border-radius: 16px;
          background: rgba(20, 40, 26, 0.55);
          border: 1px solid rgba(240, 217, 122, 0.28);
        }
        .vate-crisis {
          display: flex; gap: 10px; align-items: center;
          padding: 12px 16px; border-radius: 14px;
          background: rgba(180, 40, 40, 0.22);
          border: 1px solid rgba(255, 120, 120, 0.45);
          color: #ffe0d6;
        }
        .vate-chat {
          background: linear-gradient(180deg, rgba(16,29,22,0.85) 0%, rgba(8,15,11,0.92) 100%);
          border: 1px solid rgba(200, 230, 210, 0.18);
          border-radius: 22px;
          padding: 20px;
          box-shadow: 0 24px 60px -30px rgba(0,0,0,0.75);
        }
        .vate-messages {
          min-height: 380px; max-height: 62vh; overflow-y: auto;
          padding: 12px 4px 20px; margin-bottom: 16px;
        }
        .vate-empty {
          text-align: center; padding: 32px 20px;
        }
        .vate-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 999px;
          background: rgba(72, 105, 78, 0.35);
          border: 1px solid rgba(200,230,210,0.22);
          color: #e6efd9; font-size: 12px;
          transition: all 200ms ease;
          font-family: "IBM Plex Mono", monospace;
        }
        .vate-chip:hover {
          background: rgba(240, 217, 122, 0.15);
          border-color: rgba(240, 217, 122, 0.55);
          color: #f4ead0;
        }
        .vate-msg { display: flex; margin: 10px 0; }
        .vate-msg.user { justify-content: flex-end; }
        .vate-msg.advisor { justify-content: flex-start; }
        .vate-msg-inner {
          max-width: 78%;
          padding: 12px 16px; border-radius: 16px;
          line-height: 1.55;
          font-size: 14.5px;
        }
        .vate-msg.user .vate-msg-inner {
          background: linear-gradient(135deg, #7a9d6a 0%, #4b7255 100%);
          color: #f8f4e5;
          border-bottom-right-radius: 4px;
        }
        .vate-msg.advisor .vate-msg-inner {
          background: rgba(28, 46, 32, 0.85);
          color: #f4ead0;
          border: 1px solid rgba(200,230,210,0.18);
          border-bottom-left-radius: 4px;
        }
        .vate-md p { margin: 0 0 10px 0; }
        .vate-md p:last-child { margin-bottom: 0; }
        .vate-md strong { color: #f0d97a; }
        .vate-md em { color: #d7e8d8; }
        .vate-md h1, .vate-md h2, .vate-md h3 {
          font-family: "Cormorant Garamond", "Georgia", serif;
          color: #f0d97a; margin: 4px 0 8px;
        }
        .vate-md ul, .vate-md ol { margin: 6px 0 10px 20px; }
        .vate-md hr { border: none; border-top: 1px solid rgba(200,230,210,0.18); margin: 10px 0; }
        .vate-form {
          display: flex; gap: 10px; align-items: flex-end;
          border-top: 1px solid rgba(200,230,210,0.14);
          padding-top: 14px;
        }
        .vate-textarea {
          flex: 1; resize: none; padding: 12px 14px;
          border-radius: 14px; background: rgba(10,20,14,0.7);
          border: 1px solid rgba(200,230,210,0.22);
          color: #f4ead0; font-family: inherit; font-size: 14.5px;
          outline: none; transition: border-color 200ms ease;
        }
        .vate-textarea:focus { border-color: rgba(240, 217, 122, 0.55); }
        .vate-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 18px; border-radius: 12px;
          background: linear-gradient(135deg, #f0d97a 0%, #c9a94b 100%);
          color: #1a1108; font-weight: 600; font-size: 13px;
          font-family: "IBM Plex Mono", monospace;
          transition: transform 200ms ease, opacity 200ms ease;
        }
        .vate-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
        .vate-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .vate-btn-ghost {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 14px; border-radius: 12px;
          background: rgba(180, 40, 40, 0.15);
          border: 1px solid rgba(255, 120, 120, 0.3);
          color: #ffb8a8; font-size: 12px;
          font-family: "IBM Plex Mono", monospace;
        }
        .vate-btn-ghost:hover { background: rgba(180, 40, 40, 0.28); }
      `}</style>
    </div>
  );
}

function AgeGate({ onConfirm, t }) {
  return (
    <section data-testid={VATE.ageGate} className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 pb-24">
      <div className="vate-gate-card">
        <div className="flex items-center gap-3 mb-3">
          <Shield size={18} style={{ color: "#f0d97a" }} />
          <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: "#f4ead0" }}>
            {t("vate.gate.title")}
          </h2>
        </div>
        <p className="text-sm sm:text-base" style={{ color: "#d7e8d8" }}>
          {t("vate.gate.body")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" data-testid={VATE.ageAdultBtn}
                  onClick={onConfirm}
                  className="vate-gate-btn primary">
            <BookOpen size={16} /> {t("vate.gate.enter")}
          </button>
          <Link to="/" data-testid={VATE.ageMinorBtn} className="vate-gate-btn ghost">
            <ArrowLeft size={16} /> {t("vate.gate.decline")}
          </Link>
        </div>
      </div>

      <style>{`
        .vate-gate-card {
          background: linear-gradient(180deg, rgba(16,29,22,0.9) 0%, rgba(8,15,11,0.95) 100%);
          border: 1px solid rgba(240, 217, 122, 0.3);
          border-radius: 22px;
          padding: 28px;
          box-shadow: 0 24px 60px -30px rgba(0,0,0,0.7);
        }
        .vate-gate-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          padding: 16px 20px; border-radius: 14px;
          font-family: "IBM Plex Mono", monospace;
          font-size: 13px; font-weight: 500;
          transition: all 220ms ease;
          text-align: left;
        }
        .vate-gate-btn.primary {
          background: linear-gradient(135deg, #f0d97a 0%, #c9a94b 100%);
          color: #1a1108;
          font-weight: 600;
        }
        .vate-gate-btn.primary:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -12px rgba(240,217,122,0.5); }
        .vate-gate-btn.ghost {
          background: rgba(72, 105, 78, 0.35);
          border: 1px solid rgba(200, 230, 210, 0.28);
          color: #e6efd9;
          text-decoration: none;
        }
        .vate-gate-btn.ghost:hover {
          background: rgba(72, 105, 78, 0.55);
          border-color: rgba(200, 230, 210, 0.5);
        }
      `}</style>
    </section>
  );
}
